import {
  CENTELLIAN24_BRAND_NAME,
  CENTELLIAN24_CREDIT_CAMPAIGN,
  CENTELLIAN24_CREDIT_EXCLUDED_SKUS,
  CENTELLIAN24_CREDIT_PERCENTAGE,
} from '../../constants';
import { isWithinDateRangeInclusiveInTimeZone } from '../date';

/**
 * Strips casing, spacing, and punctuation so `Centellian 24`, `CENTELLIAN24`,
 * and `Centellian-24` all resolve to the same key. The product API is not
 * guaranteed to echo the exact `allBrands` spelling.
 */
const normalizeBrandName = (brand: string): string => brand.toLowerCase().replace(/[^a-z0-9]/g, '');

const CENTELLIAN24_BRAND_KEY = normalizeBrandName(CENTELLIAN24_BRAND_NAME);

const EXCLUDED_SKUS = new Set(CENTELLIAN24_CREDIT_EXCLUDED_SKUS);

/** True while the Extra Credit promotion window is open. */
export const isCentellian24CreditCampaignActive = (): boolean => {
  const { startDate, endDate, timeZone } = CENTELLIAN24_CREDIT_CAMPAIGN;
  return isWithinDateRangeInclusiveInTimeZone(startDate, endDate, timeZone);
};

export type Centellian24CreditInput = {
  brand: string | undefined;
  sku: string | undefined;
};

/**
 * Extra credit percentage to show on a product thumbnail, or 0 when no badge
 * should render. Callers treat 0 as "no badge" rather than branching on the
 * brand, the SKU, or the window themselves.
 *
 * A missing `sku` is treated as excluded: the badge promises store credit, so
 * an unidentifiable product must not claim it.
 */
export const getCentellian24CreditPercentage = ({ brand, sku }: Centellian24CreditInput): number => {
  if (CENTELLIAN24_CREDIT_PERCENTAGE <= 0) return 0;
  if (!brand || !sku) return 0;
  if (normalizeBrandName(brand) !== CENTELLIAN24_BRAND_KEY) return 0;
  if (EXCLUDED_SKUS.has(sku)) return 0;
  if (!isCentellian24CreditCampaignActive()) return 0;

  return CENTELLIAN24_CREDIT_PERCENTAGE;
};
